// Media storage: bytes waiting to be uploaded, and a capped cache of downloads.
//
// Pending media is the web replacement for the native client's temp files: the
// outbox row stays light, the blob sits here, and it is deleted the moment the
// upload succeeds. The download cache persists across reloads because a web app
// reloads often and has no filesystem to lean on; it is LRU-capped so it cannot
// grow without bound.

import { config } from '$lib/config';

import { db } from './db';

function asArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  // Copy: the source may be a view into a larger buffer, and IndexedDB stores
  // the whole underlying buffer otherwise.
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return copy.buffer;
}

export async function addPendingMedia(bytes: Uint8Array, contentType: string): Promise<string> {
  const id = crypto.randomUUID();
  await db.pendingMedia.put({
    id,
    bytes: asArrayBuffer(bytes),
    contentType,
    createdAtMs: Date.now(),
  });
  return id;
}

export async function pendingMediaById(
  id: string,
): Promise<{ bytes: Uint8Array; contentType: string } | undefined> {
  const row = await db.pendingMedia.get(id);
  if (row === undefined) {
    return undefined;
  }
  return { bytes: new Uint8Array(row.bytes), contentType: row.contentType };
}

export function deletePendingMedia(id: string): Promise<void> {
  return db.pendingMedia.delete(id);
}

export async function cacheMedia(input: {
  accountId: string;
  mediaId: string;
  bytes: Uint8Array;
  contentType: string;
}): Promise<void> {
  await db.mediaCache.put({
    mediaId: input.mediaId,
    accountId: input.accountId,
    bytes: asArrayBuffer(input.bytes),
    contentType: input.contentType,
    sizeB: input.bytes.length,
    lastUsedMs: Date.now(),
  });
  await pruneCache();
}

export async function cachedMedia(
  mediaId: string,
): Promise<{ bytes: Uint8Array; contentType: string } | undefined> {
  const row = await db.mediaCache.get(mediaId);
  if (row === undefined) {
    return undefined;
  }
  void db.mediaCache.update(mediaId, { lastUsedMs: Date.now() });
  return { bytes: new Uint8Array(row.bytes), contentType: row.contentType };
}

export function purgeMediaForAccount(accountId: string): Promise<number> {
  return db.mediaCache.where('accountId').equals(accountId).delete();
}

async function pruneCache(): Promise<void> {
  const rows = await db.mediaCache.orderBy('lastUsedMs').toArray();
  let total = rows.reduce((sum, r) => sum + r.sizeB, 0);
  for (const row of rows) {
    if (total <= config.mediaCacheMaxBytes) {
      return;
    }
    await db.mediaCache.delete(row.mediaId);
    total -= row.sizeB;
  }
}
